import axios from 'axios';

const API_URL = `${process.env.REACT_APP_SURVEY_API_URL}/api`;

// Crear una instancia de axios con configuración base
const axiosInstance = axios.create({
    baseURL: API_URL,
    // withCredentials: true, // Importante para CORS en Safari
});

// Interceptor para manejar los headers de forma consistente
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Headers adicionales necesarios para Safari
        config.headers['Accept'] = 'application/json';
        config.headers['Content-Type'] = 'application/json';
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Función helper para manejar errores de manera consistente
const handleApiError = (error, defaultMessage) => {
    return {
        success: false,
        message: error.response?.data?.message || defaultMessage,
        errors: error.response?.data?.errors || {},
        statusCode: error.response?.status
    };
};

/**
 * Servicio signUpApoderado simplificado
 * @param {Object} registrationData Datos de registro del usuario
 * @returns {Promise<Object>} Resultado del registro
 */
export const signUpApoderado = async (registrationData) => {
    try {
      // Para depuración: mostrar los datos que se envían
      
      // Realizar la petición
      const response = await axiosInstance.post('/register-apoderado', registrationData);
      
      // Para depuración: mostrar la respuesta completa
      
      // Devolver directamente la respuesta sin modificarla
      return response.data;
    } catch (error) {
      // Para depuración: mostrar error completo
      
      // Si hay respuesta con datos, devolver esos datos
      if (error.response && error.response.data) {
        return error.response.data;
      }
      
      // Si no hay datos en la respuesta, lanzar el error para que sea capturado
      throw error;
    }
  };

/**
 * Busca un usuario por su dirección de email
 * @param {string} email Email del usuario a buscar
 * @returns {Promise<Object>} Usuario encontrado o null
 */
export const getUserByEmail = async (email) => {
    try {
        const response = await axiosInstance.get(`/users/find-by-email/${email}`);
        return response.data.data;
    } catch (error) {
        return null;
    }
};

/**
 * Actualiza los datos de un apoderado existente
 * @param {number} userId ID del usuario a actualizar
 * @param {Object} userData Datos actualizados del usuario
 * @returns {Promise<Object>} Resultado de la actualización
 */
export const updateApoderado = async (userId, userData) => {
    try {
        const response = await axiosInstance.put(`/users/${userId}`, userData);
        return {
            success: true,
            data: response.data,
            message: 'Usuario actualizado exitosamente'
        };
    } catch (error) {
        return handleApiError(error, 'Error al actualizar usuario');
    }
};

/**
 * Busca usuarios STAFF (no alumnos) en datofy-api — UserController@index ya
 * filtra `role != 'user'` por defecto, así que sin pasar `role` devuelve
 * cualquier staff (profesor, administrador, etc.), no solo un rol específico.
 * @param {string} term Texto de búsqueda (nombre, username o email)
 * @param {Object} options
 * @param {string} [options.role] Filtrar a un rol exacto (ej: 'profesor')
 * @param {number} [options.perPage=8]
 * @returns {Promise<Array>} Lista de usuarios staff
 */
export const searchStaff = async (term, { role, perPage = 8 } = {}) => {
    if (!term || !term.trim()) return [];
    try {
        const response = await axiosInstance.get('/users', {
            params: {
                search: term.trim(),
                per_page: perPage,
                ...(role ? { role } : {}),
            },
        });
        return response.data?.data ?? [];
    } catch (error) {
        return [];
    }
};

/**
 * Obtiene la lista de todos los usuarios
 * @returns {Promise<Array>} Lista de usuarios
 */
export const getAllUsers = async () => {
    try {
        const response = await axiosInstance.get('/users');
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        return handleApiError(error, 'Error al obtener usuarios');
    }
};

/**
 * Función helper para detectar archivos de formulario/firma
 * @param {Object} file Objeto archivo
 * @returns {boolean} True si es archivo de formulario
 */
const isFormFile = (file) => {
    // Los archivos de formularios/firmas pueden identificarse por:
    // 1. No tener user_id (fueron subidos con assign_to_user: false)
    // 2. Tener assign_to_user === false en metadata
    
    if (!file.user_id) {
        return true;
    }
    
    if (file.assign_to_user === false || file.assign_to_user === "false") {
        return true;
    }
    
    return false;
};

/**
 * Obtiene los archivos de un usuario
 * @param {string} userId ID del usuario
 * @param {Object} options Opciones de filtrado
 * @param {boolean} options.excludeFormFiles - Excluir archivos de formularios/firmas (default: true)
 * @param {boolean} options.excludeCasos - Excluir archivos de survey responses de categoría "casos" (default: true)
 * @returns {Promise<Object>} Resultado con los archivos
 */
export const getUserFiles = async (userId, options = {}) => {
    const { excludeFormFiles = true, excludeCasos = true } = options;

    try {
        // Construir query params
        const params = new URLSearchParams();
        if (excludeCasos) {
            params.append('exclude_casos', '1');
        }

        const url = `/users/${userId}/files${params.toString() ? '?' + params.toString() : ''}`;
        const response = await axiosInstance.get(url);

        // Log sample file structure for debugging
        if (response.data?.data && response.data.data.length > 0) {
        }

        let files = response.data;

        // Filtrar archivos de formularios/firmas si está habilitado (filtro del lado del cliente como fallback)
        if (excludeFormFiles && files.data && Array.isArray(files.data)) {
            const originalCount = files.data.length;
            files.data = files.data.filter(file => !isFormFile(file));
        }

        if (excludeCasos) {
        }

        // El API ya devuelve { data: [...], success: true, message: "..." }
        // No necesitamos envolverlo en otro objeto
        return files;
    } catch (error) {
        return handleApiError(error, 'Error al obtener los archivos del usuario.');
    }
};

/**
 * Obtiene la lista de sitios
 * @returns {Promise<Object>} Resultado con los sitios
 */
export const getSites = async () => {
    try {
        const response = await axiosInstance.get('/sites');

        if (response.data) {
            return {
                success: true,
                data: response.data // Asegúrate que esto sea un array
            };
        }
        
        return {
            success: false,
            message: 'No se recibieron datos'
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || 'Error al obtener los sitios',
            error: error.response?.data
        };
    }
};

/**
 * Sube la foto de perfil (avatar) de un alumno a datofy-api.
 * @param {string} rut RUT del alumno (username en WP)
 * @param {File} file Archivo de imagen
 * @returns {Promise<{success, url}>}
 */
export const uploadAvatar = async (rut, file) => {
    try {
        const formData = new FormData();
        formData.append('avatar', file);

        const token = localStorage.getItem('token');
        const response = await axios.post(
            `${API_URL}/users/${encodeURIComponent(rut)}/avatar`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            }
        );
        return { success: true, url: response.data?.data?.url };
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || 'Error al subir la imagen',
        };
    }
};

/**
 * Verifica el estado de la autenticación
 * @returns {Promise<Object>} Resultado de la verificación
 */
export const checkAuthStatus = async () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            return { success: false, message: 'No token found' };
        }

        const response = await axiosInstance.get('/auth/check');
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        return handleApiError(error, 'Error al verificar la autenticación');
    }
};